import { useTranslation } from 'react-i18next'

import { SuppressedValue } from '@/components/data/SuppressedValue'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { CountryText } from '@/components/ui/CountryText'
import { formatMoney } from '@/lib/format'
import { isSuppressed, type Counterparty, type DirectoryContact, type Money } from '@/types/domain'

/* Who the counterparty is, on its page (S-22b): one strip of facts under the
 * header and «Реквизиты и контакты» below it. Since S-22e the strip's fourth
 * fact is «Средняя ставка», the statement's served average — printed, never
 * computed here. */

/** The contact the strip names: the primary one, else the first. */
function leadContact(contacts: readonly DirectoryContact[]): DirectoryContact | undefined {
  return contacts.find((contact) => contact.isPrimary) ?? contacts[0]
}

/** The white strip under the header: Страна · БИН · Контакт · Средняя ставка.
 *  The lanes are not shown at the top (owner, 2026-10-17). One column on a
 *  phone, so nothing scrolls sideways.
 *
 *  `averageRate` is the statement's `totals.averageRate` for the chosen year:
 *  «—» when no unit carries the counterparty's cost, and while the statement is
 *  loading, forbidden or unavailable — an average nobody served is not zero. */
export function CounterpartyFacts({
  record,
  averageRate,
}: {
  record: Counterparty
  averageRate?: Money | null
}) {
  const { t } = useTranslation()
  const lead = leadContact(record.contacts)
  const empty = t('common.empty')

  return (
    <Card>
      <dl
        aria-label={t('counterparties.record.facts.label')}
        className="grid grid-cols-1 gap-x-6 gap-y-3 px-[18px] py-3.5 sm:grid-cols-2 lg:grid-cols-4"
      >
        <div className="flex min-w-0 flex-col gap-1">
          <dt className="asa-eyebrow">{t('counterparties.record.facts.country')}</dt>
          <dd className="text-text-heading text-[13.5px]">
            <CountryText value={record.country} empty={empty} />
          </dd>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <dt className="asa-eyebrow">{t('counterparties.record.facts.taxId')}</dt>
          <dd className="text-text-heading asa-num text-[13.5px] break-words">
            {record.taxId || empty}
          </dd>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <dt className="asa-eyebrow">{t('counterparties.record.facts.contact')}</dt>
          <dd className="text-text-heading flex min-w-0 flex-wrap items-center gap-x-1.5 text-[13.5px]">
            {lead ? (
              <>
                <span className="break-words">{lead.label}</span>
                {/* The phone, else the email — either may be withheld by role,
                    and then it says so rather than printing nothing. */}
                <SuppressedValue
                  value={hasValue(lead.phone) ? lead.phone : lead.email}
                  label={t('common.suppressed.contact')}
                  fallback=""
                />
              </>
            ) : (
              empty
            )}
          </dd>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <dt className="asa-eyebrow">{t('counterparties.record.facts.averageRate')}</dt>
          <dd className="text-text-heading asa-num text-[13.5px]">
            {averageRate ? formatMoney(averageRate.amount, averageRate.currency) : empty}
          </dd>
        </div>
      </dl>
    </Card>
  )
}

/** «Реквизиты и контакты»: the identifiers the strip does not carry, and every
 *  contact with its details — each one withheld by role reads «Контакты
 *  ограничены», never a blank (AD-6). */
export function CounterpartyDetails({ record }: { record: Counterparty }) {
  const { t } = useTranslation()
  const empty = t('common.empty')
  const contactLabel = t('common.suppressed.contact')

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('counterparties.record.details')}</CardTitle>
      </CardHeader>
      <CardBody>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-[13px] md:grid-cols-[max-content_1fr]">
          <dt className="text-text-muted">{t('counterparties.form.taxId')}</dt>
          <dd className="break-words">{record.taxId || empty}</dd>
          <dt className="text-text-muted">{t('counterparties.form.registrationId')}</dt>
          <dd className="break-words">{record.registrationId || empty}</dd>
          <dt className="text-text-muted">{t('counterparties.form.address')}</dt>
          <dd className="break-words whitespace-pre-line">{record.address || empty}</dd>
        </dl>

        {record.contacts.length > 0 ? (
          <ul className="mt-3 flex flex-col gap-1.5 text-[13px]">
            {record.contacts.map((contact) => {
              const details = [
                contact.phone,
                contact.email,
                contact.whatsapp,
                contact.telegram,
                contact.wechat,
              ]
              const shown = details.filter(
                (value): value is string => typeof value === 'string' && value.trim() !== '',
              )
              // One marker for the row, not one per withheld field: the role
              // withholds them together.
              const withheld = details.find((value) => isSuppressed(value))
              return (
                <li key={contact.id} className="flex min-w-0 flex-wrap gap-x-4">
                  <span className="text-text-heading break-words">{contact.label}</span>
                  {shown.map((value) => (
                    <span key={value} className="break-all">
                      {value}
                    </span>
                  ))}
                  {withheld ? <SuppressedValue value={withheld} label={contactLabel} /> : null}
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-text-muted mt-3 text-[13px] font-light">
            {t('counterparties.record.noContacts')}
          </p>
        )}
      </CardBody>
    </Card>
  )
}

/** A stored value or a withheld one — anything but a blank. */
function hasValue(value: DirectoryContact['phone']): boolean {
  return typeof value !== 'string' || value.trim() !== ''
}
